import { IcdFinding } from '../../core/engines/icd-rules-engine/icd-rules-models';

/**
 * The visual states an ICD-10 chip can carry, and the one place that decides them.
 *
 * Shared because the patient file and `app-icd-code-selector` had grown two unrelated pieces of
 * markup rendering the same clinical object with different vocabularies: the selector knew about
 * coding-rule severity and the primary position, the file card knew about AI provenance and
 * removal flags, and neither knew what the other showed. The same code could therefore read as
 * "fine" on the file and "needs a more specific child" one click away in the picker — which
 * teaches a clinician that one of the two screens is lying, without saying which.
 *
 * ⚠ ADVISORY, like the rules engine it reads from. Nothing here blocks a save or reorders
 * anything. The MIT's flags are non-exhaustive and the codebook is a 2021 snapshot, so a
 * clinician with the patient in front of them can be right when the table is wrong.
 *
 * NOT an Angular thing on purpose: it is a pure function over a code and its findings, so both
 * renderers and any future one can agree by construction rather than by discipline.
 */

/** Ordered by how much a clinician needs to see it, most urgent first. */
export type IcdChipState =
  | 'error'        // a coding rule says this cannot stand as it is
  | 'warning'      // a coding rule has a reservation
  | 'flagged'      // the pipeline proposes removing it (§7 rule 6 — never automatic)
  | 'ai-applied'   // auto-accepted by the pipeline rather than typed by a person
  | 'pmb'          // PMB-linked, so it carries funding consequences
  | 'primary';     // sequence 0 — the diagnosis that leads the claim

/**
 * A code as either renderer holds it. Deliberately loose: the file card gets rows straight from
 * the server (`{ id, code, description, source, flaggedForRemoval, sequence }`) while the picker
 * may hold a codebook-enriched copy, and this has to read both.
 */
export interface IcdChipInput {
  code?: string;
  description?: string;
  pmbCodes?: string | null;
  source?: string | null;
  flaggedForRemoval?: boolean | null;
  flaggedForRemovalReason?: string | null;
  sequence?: number | null;
  onsetDate?: string | null;
  [key: string]: any;
}

export interface IcdChipView {
  /** Every state that applies, in the order declared above. */
  states: IcdChipState[];
  /**
   * The states that render an icon — i.e. `states` without `primary`.
   *
   * `primary` is carried by the leading chip's `1°` marker and its weight, not by a badge: it is
   * the normal condition of exactly one chip in every set, so an icon for it would put a
   * permanent marker on every patient and dilute the ones that mean "look at this".
   *
   * Precomputed rather than filtered in the template, because a template cannot combine `ngFor`
   * with `ngIf` on one element without a wrapper, and the obvious workaround renders an empty
   * `mat-icon` box for the primary chip.
   */
  iconStates: IcdChipState[];
  /** The single state that drives the chip's colour — the first, i.e. the most urgent. */
  primaryState: IcdChipState | null;
  /**
   * Newline-joined explanation. Every state says WHY in words, never by colour alone.
   *
   * ⚠ Render with `tooltipClass="icd-tooltip"`. A `matTooltip` collapses whitespace by default,
   * so without it these lines run together into one wall of prose — which is exactly what a
   * clinician skims past. The class sets `white-space: pre-line`.
   */
  tooltip: string;
  /** The formatted onset date, or null. Shown on the chip as well as in the tooltip. */
  onsetLabel: string | null;
}

/** Severity ranking, so a chip with both an error and a warning colours as an error. */
const STATE_ORDER: IcdChipState[] = ['error', 'warning', 'flagged', 'ai-applied', 'pmb', 'primary'];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Formats an onset date for a chip and its tooltip.
 *
 * ⚠ PARSED AS A CALENDAR DAY, NOT A TIMESTAMP. `onsetDate` is a Sequelize `DATEONLY` and arrives
 * as 'YYYY-MM-DD'. `new Date('2026-03-01')` parses that as UTC midnight and then renders it in
 * local time — which in any timezone behind UTC shows the last day of February. The column exists
 * because the date is a clinical and a billing fact, so being a day out is not cosmetic.
 *
 * Anything that is not a plain date string falls back to `Date` rather than guessing, since some
 * callers hold a value the picker has just produced.
 */
export function formatOnsetDate(value: string | null | undefined): string | null {
  if (!value) return null;

  const plain = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (plain) {
    const [, year, month, day] = plain;
    const monthName = MONTHS[Number(month) - 1];
    return monthName ? `${Number(day)} ${monthName} ${year}` : null;
  }

  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return null;
  return `${parsed.getDate()} ${MONTHS[parsed.getMonth()]} ${parsed.getFullYear()}`;
}

/**
 * Derives every state for one chip.
 *
 * @param code     the code as held by the caller
 * @param index    its position in the set; 0 is the primary diagnosis
 * @param findings the rule findings for THIS code (already filtered by `codeIndex`)
 */
export function icdChipView(
  code: IcdChipInput | null | undefined,
  index: number,
  findings: IcdFinding[] = [],
): IcdChipView {
  const states = new Set<IcdChipState>();
  const reasons: string[] = [];

  if (!code) return { states: [], iconStates: [], primaryState: null, tooltip: '', onsetLabel: null };

  reasons.push(`${code.code}${code.description ? ' - ' + code.description : ''}`);

  // Second line, always — dated or not. The chip truncates a long description, so the tooltip is
  // where the date is reliably readable; and an undated code SAYS so rather than looking
  // identical to a dated one, because "no date" is the state that needs chasing.
  const onset = formatOnsetDate(code.onsetDate);
  reasons.push(onset ? `Onset: ${onset}` : 'No onset date recorded');

  // Rule findings first: they are the only states that mean "this may be wrong".
  for (const finding of findings) {
    if (finding.severity === 'error') states.add('error');
    else if (finding.severity === 'warning') states.add('warning');
    if (finding.message) reasons.push(finding.message);
  }

  // §7 rule 6 — a removal is NEVER applied automatically, so a flagged chip is always waiting on
  // a person. That makes it more urgent than the fact that a code was auto-applied.
  if (code.flaggedForRemoval) {
    states.add('flagged');
    reasons.push(`Suggested for removal: ${code.flaggedForRemovalReason || 'no reason given'}`);
  }

  if (code.source === 'ai-auto') {
    states.add('ai-applied');
    reasons.push('Applied automatically — click for history');
  }

  if (code.pmbCodes) {
    states.add('pmb');
    reasons.push('PMB-linked');
  }

  // Position is meaning: index 0 leads the claim. Read from the index rather than `codeRole`
  // because the picker's drag-to-reorder is what a clinician actually uses to say this, and it
  // is now persisted as `sequence` so the answer survives a save.
  if (index === 0) {
    states.add('primary');
    reasons.push('Primary diagnosis');
  }

  const ordered = STATE_ORDER.filter(state => states.has(state));
  return {
    states: ordered,
    iconStates: ordered.filter(state => state !== 'primary'),
    primaryState: ordered.length ? ordered[0] : null,
    tooltip: reasons.join('\n'),
    onsetLabel: onset,
  };
}

/**
 * Joins a stored code to its codebook row, letting REFERENCE DATA win.
 *
 * ⚠ THE OBVIOUS SPREAD IS THE WRONG WAY ROUND, AND IT HID EVERY MISSING PMB FLAG.
 *
 * Both renderers used to write `{ ...reference, ...chip }`. That reads as "codebook fills in
 * what the record lacks", and it is right for every field the server omits — the MIT validity
 * flags are simply absent from a stored row, so nothing overwrites them. It is WRONG for
 * `pmbCodes`, because the server sends that column explicitly, and an explicit `null` overwrites
 * a perfectly good `"950A"` from the codebook. The client had the right answer in memory and
 * threw it away one property at a time.
 *
 * The visible symptom was a PMB code with no shield: `C79.3 Secondary malignant neoplasm of
 * brain` (PMB `950A`) rendering as an ordinary chip beside `F32.1`, which showed its shield only
 * because that row happened to be written by the AI path — the one write path that falls back to
 * the codebook. 5,254 stored rows across 696 codes are PMB and hold `null`, including
 * `I10` (901E), `I25.1` (907E), `J45.9` (157D) and `N18.9` (901L).
 *
 * Reference wins OUTRIGHT rather than only filling nulls, because PMB mapping is reference data
 * with a lifetime of its own: the stored column is a snapshot taken when the code was added, and
 * the PMB list changes. A code that has since LEFT the PMB list must stop showing a shield, which
 * a null-coalesce would never allow. `icd-code-selector` already states this model — "The MIT
 * flags are reference data, not patient data... rather than persisting a copy that would go
 * stale" — and this is the same argument applied to the field beside them.
 *
 * ⚠ DISPLAY ONLY. Neither renderer saves from the merged object (the picker submits `icdChips`,
 * the file card does not write at all), so this cannot launder a corrected value back into the
 * record. Fixing the stored column is the server's job, in the write paths that own it.
 *
 * @param chip      the row as the server sent it
 * @param reference the codebook row, or null when the code is not in the codebook
 */
export function withIcdReference<T extends IcdChipInput>(chip: T, reference: any | null): T {
  if (!reference) return chip;
  return {
    ...reference,
    ...chip,
    // The one field the server sends AND the codebook owns. `undefined` here is meaningful:
    // the enriched asset omits the key for the 34,109 codes that are not PMB.
    pmbCodes: reference.pmbCodes,
  };
}

/**
 * Orders a patient's codes for display.
 *
 * `sequence` is written from the submitted array index (`patientController` + `applyIcdUpdates`),
 * but no server read applies an `ORDER BY` — there are ~25 include sites across billing, lab
 * orders and Connect, and re-ordering all of them buys risk for no benefit when nothing
 * server-side reads position. So the ordering is applied here, once, by whoever renders.
 *
 * ⚠ MUST BE STABLE. Codes written before `sequence` was persisted all carry the column default
 * of 0, and a stable sort leaves those in exactly the order they render today rather than
 * shuffling a legacy record the first time someone opens it. The first save through the picker
 * assigns real positions for that patient, so no backfill is needed.
 */
export function orderIcdCodes<T extends IcdChipInput>(codes: T[] | null | undefined): T[] {
  if (!Array.isArray(codes)) return [];
  return codes
    .map((code, index) => ({ code, index }))
    .sort((a, b) => {
      const left = typeof a.code?.sequence === 'number' ? a.code.sequence : 0;
      const right = typeof b.code?.sequence === 'number' ? b.code.sequence : 0;
      return left === right ? a.index - b.index : left - right;
    })
    .map(entry => entry.code);
}
